import { receiveNumber, receiveString } from "../../../../controllers/adapters/receives"

/**
 * Mapea una fila del WS (prefijo `cc_*`) a la estructura interna de curso.
 */
const schemaCurso = (item: any = {}) => {

    const recibirTemas = (valor: any): string[] => {

        if (!Array.isArray(valor)) return []

        return valor
            .map((tema: any) => receiveString(typeof tema === 'string' ? tema : (tema?.nombre || tema)))
            .filter(Boolean)
    }

    return {
        id: receiveString(item.cc_id),
        titulo: receiveString(item.cc_curso),
        descripcion: receiveString(item.cc_descripcion),
        categoria: receiveString(item.categoria || item.cc_categoria),
        categoriaId: receiveString(item.cc_categoria),
        temas: recibirTemas(item.cc_tema_nombres),
        fichero: receiveString(item.cc_fichero),
        publicado: receiveString(item.cc_publicado) === '1',
        esborrado: receiveString(item.cc_esborrado) === '1',
        programado: receiveString(item.cc_programado),
    }
}

/**
 * Extrae el listado de la respuesta del WS. En cursos la lista está en
 * `data.data.data` (data -> paginación/objeto -> array), igual que en
 * actividades. Se toleran también `data.data` directo y array suelto.
 */
const extraerLista = (data: any): any[] => {

    if (!data) return []

    const esCurso = (item: any) => item && typeof item === 'object' && item.cc_id

    if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data) && Array.isArray(data.data?.data)) {

        return data.data.data.filter(esCurso)
    }

    if (Array.isArray(data?.data)) return data.data.filter(esCurso)

    if (Array.isArray(data)) return data.filter(esCurso)

    return []
}

/**
 * Detecta si un objeto es una fila de curso del WS (tiene campos `cc_*`).
 */
const esFilaCurso = (obj: any): boolean =>
    !!obj &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    Object.keys(obj).some((clave) => clave.startsWith('cc_'))

/**
 * Recupera el primer curso de la respuesta del WS (para el detalle), esté en
 * `data.curso`, plano en `data`, `curso`, ... o directamente el objeto.
 */
const extraerPrimera = (data: any): any => {

    if (!data) return {}

    if (Array.isArray(data)) return data.find(esFilaCurso) || {}

    if (esFilaCurso(data.data?.curso)) return data.data.curso

    if (esFilaCurso(data.data)) return data.data

    if (esFilaCurso(data.curso)) return data.curso

    for (const clave of ['data', 'curso', 'result', 'items']) {

        if (Array.isArray(data?.[clave])) {

            const fila = data[clave].find(esFilaCurso)

            if (fila) return fila
        }
    }

    return esFilaCurso(data) ? data : {}
}

const CursosAdapters = {

    // GET {base}/integraWeb.php?accion=obtener_cursos&actividad=...&page=N
    // La paginación viene en el objeto `data` de la respuesta (total, per_page, current_page, last_page).
    GET_CURSOS: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'obtener_cursos',
                    actividad: item.actividad || '',
                    page: item.page || 1,
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: schemaCurso,

            adapter: function (data: any) {

                const items = extraerLista(data).map((item: any) => this.schema(item))

                const paginacion = data?.data || data || {}

                return {
                    items,
                    paginacion: {
                        total: receiveNumber(paginacion.total),
                        per_page: receiveNumber(paginacion.per_page),
                        page: receiveNumber(paginacion.current_page),
                        last_page: receiveNumber(paginacion.last_page),
                    }
                }
            }
        },
    },

    // GET {base}/integraWeb.php?accion=mostrar_detalle_curso&id=N
    // Se usa en el modal de previsualización de un curso.
    // OJO: el WS despacha este detalle por `modulo` (no por `accion`). Se envían
    // ambos parámetros para que funcione con ambas versiones del WS.
    GET_CURSO: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'mostrar_detalle_curso',
                    modulo: item.modulo || 'mostrar_detalle_curso',
                    id: item.id ?? item.cc_id,
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: schemaCurso,

            adapter: function (data: any) {

                const curso = this.schema(extraerPrimera(data))

                return {
                    data: curso,
                }
            }
        },
    },
}

export {
    CursosAdapters
}